import ApiService from './api.service'
import type {
  NoteResponse,
  NoteListResponse,
  NoteSearchResponse,
  AllNotesResponse,
  NoteData,
  NoteSearchOptions
} from '@/types/services'

/**
 * Service for managing project notes API endpoints
 */
class ProjectNotesService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Notes Error'
      }
    })
  }

  /**
   * Get all notes for a project with pagination
   */
  async getProjectNotes(
    projectId: string, 
    options: NoteSearchOptions = {}
  ): Promise<NoteListResponse | null> {
    const {
      page = 0,
      size = 10,
      sort = 'createdAt,desc'
    } = options

    const params = { page, size, sort }
    return this.get(`/api/project/${projectId}/notes`, params)
  }

  /**
   * Get all notes for a project without pagination
   */
  async getAllProjectNotes(projectId: string): Promise<AllNotesResponse | null> {
    return this.get(`/api/project/${projectId}/notes/all`)
  }

  /**
   * Get a specific note by ID
   */
  async getNoteById(projectId: string, noteId: string): Promise<NoteResponse | null> {
    return this.get(`/api/project/${projectId}/notes/${noteId}`)
  }

  /**
   * Create a new note
   */
  async createNote(projectId: string, noteData: NoteData): Promise<NoteResponse | null> {
    return this.post(`/api/project/${projectId}/notes`, noteData)
  }

  /**
   * Update an existing note
   */
  async updateNote(
    projectId: string, 
    noteId: string, 
    noteData: NoteData
  ): Promise<NoteResponse | null> {
    return this.put(`/api/project/${projectId}/notes/${noteId}`, noteData)
  }

  /**
   * Delete a note
   */
  async deleteNote(projectId: string, noteId: string): Promise<NoteResponse | null> {
    return this.delete(`/api/project/${projectId}/notes/${noteId}`)
  }

  /**
   * Get notes by priority
   */
  async getNotesByPriority(
    projectId: string, 
    priority: string, 
    options: NoteSearchOptions = {}
  ): Promise<NoteListResponse | null> {
    const {
      page = 0,
      size = 10,
      sort = 'createdAt,desc'
    } = options

    const params = { page, size, sort }
    return this.get(`/api/project/${projectId}/notes/priority/${priority}`, params)
  }

  /**
   * Search notes by text
   */
  async searchNotes(
    projectId: string, 
    searchText: string, 
    options: NoteSearchOptions = {}
  ): Promise<NoteSearchResponse | null> {
    const {
      page = 0,
      size = 10,
      sort = 'createdAt,desc'
    } = options

    const params = { q: searchText, page, size, sort }
    return this.get(`/api/project/${projectId}/notes/search`, params)
  }
}

export default new ProjectNotesService()
